/**
 * SavedOnePagersCard — Stage 10 visible accumulated value.
 *
 * Surfaces the user's saved chat one-pagers on Home as a calm asset list.
 * Audit prescription: "visible asset accumulation drives renewal more than
 * feature lists do." Auto-hides when the user has none yet so brand-new
 * accounts don't see scaffolding.
 *
 * Data: GET /api/chat/saved-one-pagers → { count, items[]: { id, session_id,
 *   teaser, for_appointment, saved_at } }.
 *
 * UX: tap the card → /chat?session=<sessionId>&message=<messageId> so the
 * exact saved reply is restored and visible.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../../services/api';
import { motion } from 'framer-motion';
import { FileArrowDown, Stethoscope, ArrowRight } from '@phosphor-icons/react';

export const SavedOnePagersCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/chat/saved-one-pagers`);
        if (!cancelled) setData(d);
      } catch { /* graceful — card just hides */ }
      finally { if (!cancelled) setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loaded) return null;
  if (!data || (data.count || 0) === 0) return null;

  const top = (data.items || []).slice(0, 2);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="rounded-2xl border border-[#ECE8E1] bg-white overflow-hidden"
      data-testid="saved-one-pagers-card"
    >
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center gap-2 mb-2">
          <FileArrowDown size={12} weight="fill" className="text-[#2FB3A0]" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Your one-pagers
          </span>
        </div>
        <p
          className="font-serif text-[20px] sm:text-[22px] leading-tight tracking-[-0.005em] text-[#0E1C2E]"
          data-testid="saved-one-pagers-headline"
        >
          {data.count === 1
            ? 'You have 1 one-pager saved.'
            : `You have ${data.count} one-pagers saved.`}
        </p>
        <p className="text-[12.5px] text-slate-500 mt-1.5 leading-relaxed">
          Ready to bring to your next visit, or revisit any time.
        </p>
      </div>

      {/* Top 2 teasers */}
      {top.length > 0 && (
        <div className="px-5 pb-4 space-y-2.5" data-testid="saved-one-pagers-list">
          {top.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => navigate(`/chat?session=${p.session_id}`)}
              className="w-full text-left rounded-xl bg-[#FBFAF6] border border-[#ECE8E1] hover:border-[#CFC8BB] hover:bg-white px-4 py-3 transition-colors group"
              data-testid={`saved-one-pager-${p.id}`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                {p.for_appointment ? (
                  <Stethoscope size={11} weight="duotone" className="text-[#0E1C2E]" />
                ) : (
                  <FileArrowDown size={11} weight="duotone" className="text-[#2FB3A0]" />
                )}
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  {p.for_appointment ? 'For your next visit' : 'One-pager'}
                </span>
              </div>
              <p className="text-[13px] text-[#0E1C2E] leading-relaxed line-clamp-2">{p.teaser}</p>
            </button>
          ))}
        </div>
      )}

      {/* Footer link — only when there are more than what we showed */}
      {data.count > top.length && (
        <button
          type="button"
          onClick={() => navigate('/chat')}
          className="w-full px-5 py-3 bg-[#FBFAF6] border-t border-[#ECE8E1] inline-flex items-center justify-between gap-2 group"
          data-testid="saved-one-pagers-see-all"
        >
          <span className="text-[13px] font-medium text-[#0E1C2E]">See all {data.count}</span>
          <ArrowRight size={13} weight="bold" className="text-[#2FB3A0] group-hover:translate-x-0.5 transition-transform" />
        </button>
      )}
    </motion.div>
  );
};

export default SavedOnePagersCard;
